import { callQuery, GET_FILE_AT_VERSION } from "../../../amplify/api-gateway/query";
import { PatientVisit } from "../../../amplify/api-gateway/search-patients";
import { readBinaryFile } from "../../../amplify/s3/storage";

class DashboardLLMCache {
    private static instance: DashboardLLMCache;
    private cache: Map<string, any>;
    private readonly CACHE_DURATION = 0; // Turn off cache

    private constructor() {
        this.cache = new Map();
    }

    public static getInstance(): DashboardLLMCache {
        if (!DashboardLLMCache.instance) {
            DashboardLLMCache.instance = new DashboardLLMCache();
        }
        return DashboardLLMCache.instance;
    }

    private generateCacheKey(type: string, patient: PatientVisit, versionId?: string) {
        let key = `${type}-${patient.csn}`;
        if (versionId) key += `-v${versionId}`;
        return key;
    }

    public async get(
        type: string,
        patient: PatientVisit,
        tenant: string,
        versionId?: string
    ) {
        if (!patient || !type) return undefined;

        const cacheKey = this.generateCacheKey(type, patient, versionId);

        // If caching is disabled (CACHE_DURATION = 0), skip cache entirely
        if (this.CACHE_DURATION === 0) {
            try {
                let data;
                if (versionId) {
                    // Fetch specific version via GET_FILE_AT_VERSION
                    const response: any = await callQuery({
                        operation: GET_FILE_AT_VERSION,
                        mrn: patient.mrn,
                        csn: patient.csn,
                        file_type: type,
                        version_id: versionId,
                    });
                    data = response;
                } else {
                    // Fetch latest version from S3
                    const remoteData = await readBinaryFile(
                        `llm_dashboard/mrn=${patient.mrn}/csn=${patient.csn}/${type}.json`,
                        tenant,
                        `ed-llm-dashboard`
                    );
                    const decoder = new TextDecoder();
                    data = JSON.parse(decoder.decode(remoteData));
                }
                return data;
            } catch (e) {
                return undefined;
            }
        }

        // Check local cache first (only when caching is enabled)
        const cachedData = this.cache.get(cacheKey);
        if (cachedData) {
            console.log(`FOUND local cache for key: ${cacheKey}`);
            if (Date.now() - cachedData.timestamp > this.CACHE_DURATION) {
                this.cache.delete(cacheKey);
            } else {
                return cachedData;
            }
        }

        try {
            let data;
            if (versionId) {
                // Fetch specific version via GET_FILE_AT_VERSION
                const response: any = await callQuery({
                    operation: GET_FILE_AT_VERSION,
                    mrn: patient.mrn,
                    csn: patient.csn,
                    file_type: type,
                    version_id: versionId,
                });
                data = response;
            } else {
                // Fetch latest version from S3
                const remoteData = await readBinaryFile(
                    `llm_dashboard/mrn=${patient.mrn}/csn=${patient.csn}/${type}.json`,
                    tenant,
                    `ed-llm-dashboard`
                );
                const decoder = new TextDecoder();
                data = JSON.parse(decoder.decode(remoteData));
            }

            // Cache the data locally (only when caching is enabled)
            if (this.CACHE_DURATION > 0) {
                this.cache.set(cacheKey, { ...data, timestamp: Date.now() });
            }

            return data;
        } catch (e) {
            return undefined;
        }
    }

    public clear(): void {
        this.cache.clear();
    }
}

export const dashboardLLMCache = DashboardLLMCache.getInstance();
